import { SelectionModel } from '@angular/cdk/collections';
import { DatePipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  effect,
  inject,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import {
  MatPaginator,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import {
  MatColumnDef,
  MatTableDataSource,
  MatTableModule,
} from '@angular/material/table';
import { RouterLink } from '@angular/router';

import { ReportService } from '@modules/job/details/resources/edit/report/report.service';
import { EmployeeStore } from '@modules/job/details/resources/employee.store';
import { JobResourceTypeStore } from '@modules/job/details/resources/job-resource-type.store';
import { ClickOutsideDirective } from '@shared/directives/click-outside.directive';

import { ExportType, Overview } from '../overview.service';
import { OverviewStore } from '../overview.store';
import { OverviewExportStore } from '../overview-export.store';
import { ReportDetailDialogComponent } from './report-dialog/report-dialog.component';

@Component({
  imports: [
    MatIconModule,
    MatColumnDef,
    DatePipe,
    MatTableModule,
    MatIconModule,
    MatCheckboxModule,
    MatSortModule,
    RouterLink,
    MatPaginatorModule,
    ClickOutsideDirective,
  ],
  selector: 'norupe-overview-table',
  styles: ``,
  templateUrl: './overview-table.component.html',
})
export class OverviewTableComponent implements AfterViewInit, OnDestroy {
  readonly overviewStore = inject(OverviewStore);
  readonly employeeStore = inject(EmployeeStore);
  readonly overviewExportStore = inject(OverviewExportStore);
  readonly resourceTypeStore = inject(JobResourceTypeStore);
  readonly dialog = inject(MatDialog);
  private readonly reportService = inject(ReportService);

  isDownloadDropdownOpen = false;
  displayedColumns: string[] = [
    'select',
    'assignedTo',
    'type',
    'dateDone',
    'value',
    'unit',
    'timeSpent',
    'job',
    'notes',
  ];
  selection = new SelectionModel<Overview>(true, []);
  dataSource = new MatTableDataSource<Overview>([]);

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor() {
    effect(() => {
      this.dataSource.data = this.overviewStore.entities();
    });

    effect(() => {
      const count = this.overviewStore.count();
      const pageSize = this.overviewStore.pageSize();
      const pageIndex = this.overviewStore.pageIndex();

      this.dataSource.data = this.overviewStore.entities();

      if (this.paginator) {
        this.paginator.length = count;
        this.paginator.pageSize = pageSize;
        this.paginator.pageIndex = pageIndex;
      }
    });
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;

    this.dataSource.sortingDataAccessor = (
      item: Overview,
      property: string,
    ) => {
      switch (property) {
        case 'assignedTo': {
          const employee = this.employeeStore.entityMap()[item.assigned_to];
          return employee ? `${employee.first_name} ${employee.last_name}` : '';
        }
        case 'type':
          return this.resourceTypeStore.entityMap()[item.type]?.name ?? '';
        case 'dateDone':
          return new Date(item.date_done).getTime();
        case 'timeSpent':
          return item.time_spent;
        case 'job':
          return item.job_title;
        default:
          return item[property as keyof Overview] as string | number;
      }
    };
  }
  toggleDropdown() {
    this.isDownloadDropdownOpen = !this.isDownloadDropdownOpen;
  }

  onPageChange(event: PageEvent) {
    //clear the selection
    this.selection.clear();

    //update pagination and load in next page with the same filters
    this.overviewStore.updatePagination(event.pageIndex, event.pageSize);
    this.overviewStore.loadOverview({ ...this.overviewStore.filters() });
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this.dataSource.data);
  }
  checkboxLabel(row?: Overview): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.id + 1}`;
  }

  openReportDialog(report: { report_title: string; report: string }) {
    // eslint-disable-next-line rxjs-x/no-ignored-error
    this.reportService.getReportByUrl(report.report).subscribe((data) => {
      this.dialog.open(ReportDetailDialogComponent, {
        data: {
          ...data,
          report_title: report.report_title,
          report_url: report.report,
        },
        width: '500px',
      });
    });
  }

  downloadReport(exportType: ExportType) {
    if (this.selection.selected.length > 0) {
      const selectedIds = this.selection.selected.map((item) => item.id);
      this.overviewExportStore.downloadReport({
        exportType,
        filters: {
          ...this.overviewStore.filters(),
          ids: selectedIds,
        },
      });
    } else {
      this.overviewExportStore.downloadReport({
        exportType,
        filters: this.overviewStore.filters(),
      });
    }
  }

  ngOnDestroy() {
    this.overviewStore.removeAllEntities();
  }
}
